import * as React from "react";
import type { PropertyImage } from "@/data/types";
import { Reveal, Overline } from "./primitives";

interface Props {
  images: PropertyImage[];
  floorPlan?: PropertyImage;
}

// Restrained gallery: skip hero (index 0), show a few additional photos + floor plan.
export const PropertyGallery: React.FC<Props> = ({ images, floorPlan }) => {
  const gallery = (images || []).slice(1, 7);
  const items = floorPlan ? [...gallery, floorPlan] : gallery;
  if (items.length === 0) return null;

  return (
    <section
      className="w-full bg-ivory pb-20 md:pb-28"
      data-testid="property-gallery"
      aria-label="Property visuals"
    >
      <div className="mx-auto w-full max-w-5xl px-6 md:px-10">
        <Reveal>
          <Overline>Property visuals</Overline>
          <h2 className="mt-4 font-serif text-3xl leading-tight text-charcoal md:text-4xl">
            Selected listing images.
          </h2>
        </Reveal>

        <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-6">
          {items.map((img, i) => {
            const isFloor = floorPlan && img.url === floorPlan.url;
            const wide = !isFloor && items.length % 2 !== 0 && i === 0;
            return (
              <Reveal
                key={`${img.url}-${i}`}
                delay={i * 0.08}
                className={wide ? "sm:col-span-2" : undefined}
              >
                <figure className="group overflow-hidden">
                  <img
                    src={img.url}
                    alt={img.alt || (isFloor ? "Floor plan" : "Property photograph")}
                    loading="lazy"
                    decoding="async"
                    className={`w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.04] ${
                      isFloor
                        ? "aspect-[4/3] bg-ivory-alt object-contain"
                        : wide
                          ? "aspect-[16/9]"
                          : "aspect-[4/3]"
                    }`}
                  />
                  {(img.caption || isFloor) && (
                    <figcaption className="mt-3 text-xs uppercase tracking-[0.18em] text-charcoal-muted">
                      {img.caption || (isFloor ? "Floor plan" : undefined)}
                    </figcaption>
                  )}
                </figure>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};
